/**
 * Junto Frontend BookingPopupBase Component
 * 
 * Base component for booking-related popups with shared styles and utilities.
 * Theme-agnostic and reusable across different studio brands.
 */

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Modal } from '../ui/index.js';
import { solRoomTheme } from '../../themes/sol-room.js';
import { useAuth } from '../../contexts/ClerkAuthContext';

// Shared styled components
export const PopupTitle = styled.h3`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
  text-align: center;
`;

export const ClassInfo = styled.div`
  background: ${props => props.theme?.colors?.neutral?.lightest || solRoomTheme.colors.neutral.lightest};
  border: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  border-radius: 8px;
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

export const ClassName = styled.h4`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} 0;
`;

export const ClassDetails = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  line-height: 1.4;
`;

export const NoPaymentMessage = styled.div`
  background: ${props => props.theme?.colors?.neutral?.lightest || solRoomTheme.colors.neutral.lightest};
  border: 1px solid ${props => props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium};
  border-radius: 8px;
  padding: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  text-align: center;
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

export const NoPaymentTitle = styled.h5`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} 0;
`;

export const NoPaymentText = styled.p`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  margin: 0 0 ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
`;

export const PurchaseButtonsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  justify-content: center;
  flex-wrap: wrap;
`;

export const PurchaseButton = styled(Link)`
  font-family: ${props => props.theme?.typography?.button?.fontFamily || solRoomTheme.typography.button.fontFamily};
  font-size: ${props => props.theme?.typography?.button?.fontSize || solRoomTheme.typography.button.fontSize};
  font-weight: ${props => props.theme?.typography?.button?.fontWeight || solRoomTheme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme?.typography?.button?.letterSpacing || solRoomTheme.typography.button.letterSpacing};
  text-transform: ${props => props.theme?.typography?.button?.textTransform || solRoomTheme.typography.button.textTransform};
  
  background: transparent;
  color: ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
  border: 1px solid ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: 8px 16px;
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  text-decoration: none;
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  justify-content: center;
  
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || solRoomTheme.breakpoints.mobile}) {
    gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  }
`;

export const ActionButton = styled.button`
  font-family: ${props => props.theme?.typography?.button?.fontFamily || solRoomTheme.typography.button.fontFamily};
  font-size: calc(${props => props.theme?.typography?.button?.fontSize || solRoomTheme.typography.button.fontSize} * 0.875);
  font-weight: ${props => props.theme?.typography?.button?.fontWeight || solRoomTheme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme?.typography?.button?.letterSpacing || solRoomTheme.typography.button.letterSpacing};
  text-transform: ${props => props.theme?.typography?.button?.textTransform || solRoomTheme.typography.button.textTransform};
  
  background: ${props => props.$primary ? (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons) : 'transparent'};
  color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons)};
  border: 1px solid ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.$primary ? (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium) : 'transparent'};
    color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium)};
    border-color: ${props => props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium};
    
    &:hover {
      background: ${props => props.$primary ? (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium) : 'transparent'};
      color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium)};
      transform: none;
    }
  }
`;

// Helper function to find minimum cost credit package for a class
export const findMinCostCreditPackage = async (classData) => {
  try {
    // Import the API service
    const { membershipService } = await import('../../api/migratedJuntoAPI.js');
    
    // Get all credit packages
    const creditPackages = await membershipService.getCreditPackages();
    const packages = creditPackages.data || creditPackages || [];
    
    // Get the class category
    const classCategory = classData?.category?.id || classData?.class_category_id;
    
    if (!classCategory) {
      // Fallback: return the cheapest package overall
      if (packages.length > 0) {
        const sortedPackages = packages.sort((a, b) => (a.price || a.package_price || 0) - (b.price || b.package_price || 0));
        return sortedPackages[0];
      }
      return null;
    }
    
    // Filter packages that can be used for this class category
    const applicablePackages = packages.filter(pkg => {
      // Check if package has items with credits that cover this class category
      if (pkg.items && Array.isArray(pkg.items)) {
        const hasMatchingCategory = pkg.items.some(item => {
          const itemCategories = item.class_categories || [];
          return itemCategories.includes(classCategory);
        });
        return hasMatchingCategory;
      }
      return false;
    });
    
    if (applicablePackages.length === 0) {
      // Fallback: return the cheapest package overall
      if (packages.length > 0) {
        const sortedPackages = packages.sort((a, b) => (a.price || a.package_price || 0) - (b.price || b.package_price || 0));
        return sortedPackages[0];
      }
      return null;
    }
    
    // Find the package with minimum total cost
    const packageWithCosts = applicablePackages.map(pkg => {
      const price = pkg.price || pkg.package_price || 0;
      return {
        ...pkg,
        totalCost: price
      };
    });
    
    // Sort by total cost and return the cheapest
    packageWithCosts.sort((a, b) => a.totalCost - b.totalCost);
    
    return packageWithCosts[0];
  } catch (error) {
    console.error('Failed to find min cost credit package:', error);
    return null;
  }
};

// Helper function to check if a class is free
export const isClassFree = (classData) => {
  return classData?.cost === 0 || classData?.price === 0 || classData?.base_cost === 0;
};

// Shared utility functions
export const formatDate = (dateString) => {
  // Treat all dates as timezone-naive - parse the date string components directly
  // to avoid any timezone conversions
  const date = new Date(dateString);
  
  // If the string looks like an ISO date with timezone (ends with Z or has offset),
  // strip the timezone part and parse as local time
  if (typeof dateString === 'string' && (dateString.endsWith('Z') || dateString.match(/[+-]\d{2}:\d{2}$/))) {
    // Remove timezone indicator and parse as local time
    const localDateString = dateString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    const [datePart] = localDateString.split('T');
    const [year, month, day] = datePart.split('-');
    
    // Create date in local timezone using components
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return localDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // For other date formats, use as-is
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatTime = (timeString) => {
  // Treat time as timezone-naive
  if (typeof timeString === 'string' && (timeString.endsWith('Z') || timeString.match(/[+-]\d{2}:\d{2}$/))) {
    // Remove timezone indicator and parse as local time
    const localTimeString = timeString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    const date = new Date(localTimeString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
  
  const date = new Date(timeString);
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

// Shared payment validation logic - updated for real Junto Pay API
export const usePaymentValidation = (classData) => {
  const { accountData } = useAuth();
  
  const hasPaymentOptions = () => {
    if (!accountData) return false;

    // Check for active unlimited membership
    const activeMembership = accountData?.memberships?.find(membership => 
      membership.status === 'active' && 
      membership.membership_type === 'unlimited'
    );
    if (activeMembership) {
      return true;
    }

    // Check for valid membership with remaining classes
    const validMembership = accountData?.memberships?.find(membership => 
      membership.status === 'active' && 
      membership.classes_remaining > 0
    );
    if (validMembership) {
      return true;
    }
    
    // Check wallet for usable credits
    if (accountData?.wallet?.credits?.length > 0) {
      const classCategory = classData?.category?.id || classData?.class_category_id;
      
      const usableCredits = accountData.wallet.credits.find(credit => {
        // Credit must not be expired
        if (credit.expiry_date && new Date(credit.expiry_date) < new Date()) {
          return false;
        }
        
        // Credit must have remaining uses
        if (credit.remaining_uses <= 0) {
          return false;
        }
        
        // Check if credit can be used for this class category
        if (classCategory && credit.class_categories) {
          return credit.class_categories.includes(classCategory);
        }
        
        // Fallback: check by credit type name (less reliable)
        const creditType = credit.credit_type?.name?.toLowerCase() || '';
        const className = classData?.name?.toLowerCase() || '';
        
        if (creditType.includes('group') && (className.includes('mat') || className.includes('reformer'))) {
          return true;
        }
        if (creditType.includes('private') && className.includes('private')) {
          return true;
        }
        
        return creditType.includes('universal') || creditType.includes('any');
      });
      
      if (usableCredits) {
        return true;
      }
    }
    
    // Check wallet cash balance (if payment method is available)
    if (accountData?.wallet?.cash_balance > 0) {
      return true;
    }
    
    return false;
  };
  
  const getPaymentMethods = () => {
    const methods = [];
    
    if (!accountData) return methods;

    // Add active memberships
    const activeMembership = accountData?.memberships?.find(membership => 
      membership.status === 'active' && 
      (membership.membership_type === 'unlimited' || membership.classes_remaining > 0)
    );
    if (activeMembership) {
      methods.push({
        type: 'membership',
        label: `${activeMembership.membership_type} Membership`,
        subtitle: activeMembership.membership_type === 'unlimited' ? 'Unlimited classes' : `${activeMembership.classes_remaining} classes remaining`
      });
    }

    // Add usable credits
    if (accountData?.wallet?.credits?.length > 0) {
      const classCategory = classData?.category?.id || classData?.class_category_id;
      
      accountData.wallet.credits.forEach(credit => {
        if (credit.remaining_uses > 0 && 
            (!credit.expiry_date || new Date(credit.expiry_date) > new Date())) {
          
          const canUse = classCategory ? 
            credit.class_categories?.includes(classCategory) : 
            true; // Fallback to allowing if no category info
            
          if (canUse) {
            methods.push({
              type: 'credit',
              id: credit.id,
              label: credit.credit_type?.name || 'Class Credit',
              subtitle: `${credit.remaining_uses} remaining`
            });
          }
        }
      });
    }

    // Add cash payment if balance available
    if (accountData?.wallet?.cash_balance > 0) {
      methods.push({
        type: 'cash',
        label: 'Wallet Cash',
        subtitle: `$${accountData.wallet.cash_balance.toFixed(2)} available`
      });
    }
    
    return methods;
  };
  
  return { hasPaymentOptions, getPaymentMethods };
};

// Shared class info component
export const ClassInfoSection = ({ classData, theme = solRoomTheme }) => {
  if (!classData) return null;
  
  return (
    <ClassInfo theme={theme}>
      <ClassName theme={theme}>{classData.name}</ClassName>
      <ClassDetails theme={theme}>
        {classData.start_time ? formatDate(classData.start_time) : formatDate(new Date())} at {classData.start_time ? formatTime(classData.start_time) : classData.time}<br />
        Instructor: {classData.instructor?.name || classData.instructor || 'TBA'}<br />
        Location: {classData.location?.name || classData.location || 'TBA'} • Duration: {classData.duration || Math.round((new Date(classData.end_time) - new Date(classData.start_time)) / (1000 * 60))} min
      </ClassDetails>
    </ClassInfo>
  );
};

// Shared no payment options component
export const NoPaymentOptionsSection = ({ onClose, onDirectPurchase, theme = solRoomTheme }) => (
  <NoPaymentMessage theme={theme}>
    <NoPaymentTitle theme={theme}>No Direct Reservation Options Available</NoPaymentTitle>
    <NoPaymentText theme={theme}>
      You don't have a valid membership or credits, in your wallet to use to book this class.
    </NoPaymentText>
    <NoPaymentText theme={theme} style={{ marginTop: theme?.spacing?.sm || solRoomTheme.spacing.sm, marginBottom: theme?.spacing?.sm || solRoomTheme.spacing.sm }}>
      You can directly purchase a credit and reserve this class:
    </NoPaymentText>
    <PurchaseButtonsContainer theme={theme}>
      <PurchaseButton 
        as="button" 
        onClick={onDirectPurchase} 
        theme={theme}
      >
        Buy + Reserve
      </PurchaseButton>
    </PurchaseButtonsContainer>
    <NoPaymentText theme={theme} style={{ marginTop: theme?.spacing?.lg || solRoomTheme.spacing.lg, marginBottom: theme?.spacing?.sm || solRoomTheme.spacing.sm }}>
      Or you can buy a credit package or start a new membership of your choice:
    </NoPaymentText>
    <PurchaseButtonsContainer theme={theme}>
      <PurchaseButton to="/purchase#memberships" onClick={onClose} theme={theme}>
        Buy Membership
      </PurchaseButton>
      <PurchaseButton to="/purchase#credits" onClick={onClose} theme={theme}>
        Buy Credits
      </PurchaseButton>
    </PurchaseButtonsContainer>
  </NoPaymentMessage>
);

// Alternative button container with greyed out purchase button
export const NoPaymentButtonContainer = ({ onClose, buttonText = "Close", theme = solRoomTheme }) => (
  <ButtonContainer theme={theme}>
    <ActionButton onClick={onClose} theme={theme}>
      {buttonText}
    </ActionButton>
    <ActionButton $primary disabled theme={theme}>
      Reserve
    </ActionButton>
  </ButtonContainer>
);

// Base popup wrapper
const BookingPopupBase = ({ isOpen, onClose, title, maxWidth = "500px", children, theme = solRoomTheme }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth={maxWidth} theme={theme}>
      <PopupTitle theme={theme}>{title}</PopupTitle>
      {children}
    </Modal>
  );
};

export default BookingPopupBase;